import { useState } from 'react';

import { useAppSelector as useSelector } from 'hooks/reduxHooks';
import { getBackendJourneyErrors } from 'helpers/error.helper';
import { getIsJourneyActiveOrScheduled, getJourneyStatusLabels } from 'store/journey/journey.selector';
import { JOURNEY_STATUSES, JOURNEY_STATUS_LABELS, JourneyErrorAction } from 'store/journey/journey.type';
import { JourneyError } from 'validators/ExperienceCanvas/types';

/** Title shown by the error variant, keyed by the failed action (ported verbatim from v1). */
export const ACTION_TITLE: Record<JourneyErrorAction, string> = {
  save: 'Unable to save experience',
  launch: 'Unable to launch experience',
  schedule: 'Unable to schedule experience',
  activate: 'Unable to activate experience',
  deactivate: 'Unable to deactivate experience',
};

/** Which glyph the launch button shows, derived from the status label. */
export type LaunchIconKind = 'rocket' | 'calendar';

export const DISABLED_LAUNCH_MESSAGE = 'Complete tasks to enable launch';
export const LAUNCH_BLOCKING_ERROR_MESSAGE = 'Resolve the reported errors and save before launching';

export type LaunchPopoverState =
  | { kind: 'error'; title: string; errors: JourneyError[] }
  | { kind: 'disabled'; launchLabel: string; launchIcon: LaunchIconKind; message: string }
  | { kind: 'ready'; launchLabel: string; launchIcon: LaunchIconKind }
  | { kind: 'live'; deactivateLabel: string }
  | { kind: 'scheduled'; deactivateLabel: string };

function experienceActionLabel(statusLabel: JOURNEY_STATUS_LABELS): string {
  return `${statusLabel} experience`;
}

function launchIconFor(label: JOURNEY_STATUS_LABELS): LaunchIconKind {
  return label === JOURNEY_STATUS_LABELS.SCHEDULE ? 'calendar' : 'rocket';
}

/**
 * Derives the unified popover's visual state from existing journey selectors, and owns the
 * dismiss-then-reveal behaviour for the error variant.
 *
 * State priority (highest first):
 *  1. **error** — a non-dismissed backend failure (`richerror` → top-level errors). Dismissing via
 *     the X reveals whichever launch state is underneath; a *fresh* `richerror` re-opens it.
 *  2. **live / scheduled** — `getIsJourneyActiveOrScheduled`, split by ACTIVE vs SCHEDULED.
 *  3. **disabled vs ready** — `getJourneyStatusLabels().launch.isDisabled`.
 */
export default function useLaunchPopoverState(): {
  state: LaunchPopoverState;
  dismissError: () => void;
} {
  const richerror = useSelector((state) => state.te.journey.richerror);
  const topLevelErrors = useSelector((state) => {
    const beErrors = Array.isArray(state.te.journey.richerror?.errors) ? state.te.journey.richerror.errors : [];
    return getBackendJourneyErrors(beErrors, state).topLevelErrors;
  });
  const action = useSelector((state) => state.te.journey.errorContext?.action ?? 'save');
  const hasLaunchBlockingError = useSelector((state) => !!state.te.journey.hasLaunchBlockingError);
  const isActiveOrScheduled = useSelector((state) => getIsJourneyActiveOrScheduled(state.te));
  const status = useSelector((state) => state.te.journey.status);
  const launchLabels = useSelector((state) => getJourneyStatusLabels(state).launch);

  const [dismissedRichError, setDismissedRichError] = useState<typeof richerror>();
  const isDismissed = richerror != null && dismissedRichError === richerror;
  const dismissError = () => setDismissedRichError(richerror);

  let state: LaunchPopoverState;
  if (!isDismissed && topLevelErrors.length > 0) {
    state = { kind: 'error', title: ACTION_TITLE[action], errors: topLevelErrors };
  } else if (isActiveOrScheduled) {
    const deactivateLabel = experienceActionLabel(JOURNEY_STATUS_LABELS.DEACTIVATE);
    state =
      status === JOURNEY_STATUSES.ACTIVE
        ? { kind: 'live', deactivateLabel }
        : { kind: 'scheduled', deactivateLabel };
  } else if (launchLabels.isDisabled || hasLaunchBlockingError) {
    state = {
      kind: 'disabled',
      launchLabel: experienceActionLabel(launchLabels.statusLabel),
      launchIcon: launchIconFor(launchLabels.statusLabel),
      message: hasLaunchBlockingError ? LAUNCH_BLOCKING_ERROR_MESSAGE : DISABLED_LAUNCH_MESSAGE,
    };
  } else {
    state = {
      kind: 'ready',
      launchLabel: experienceActionLabel(launchLabels.statusLabel),
      launchIcon: launchIconFor(launchLabels.statusLabel),
    };
  }

  return { state, dismissError };
}
